import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminShell from '../../components/admin/AdminShell';
import LoadingDNA from '../../components/LoadingDNA';
import { SectionCard, StatusBadge } from '../../components/admin/AdminPrimitives';
import { getCompanyEmployees, isApiError } from '../../services/dashboardService';
import {
  bulkDecisionExpenseSplits,
  getAdminApprovalSlaBreaches,
  ControlTowerStreamEvent,
  getExpenseEvents,
  getReconciliationExceptionsReport,
  isExpenseApiError,
  remindManagersForSplits,
  runEscalationScan,
  subscribeControlTowerStream,
} from '../../services/expenseService';
import { formatCurrencyINRFull, formatDateIN, normalizeWorkflowStatus } from '../../utils/adminFormat';

type QueueItem = {
  id: string;
  type: 'SLA_BREACH' | 'APPROVAL_PENDING' | 'POLICY_VIOLATION' | 'RECON_GAP';
  severity: 'danger' | 'warning' | 'neutral';
  title: string;
  detail: string;
  owner: string;
  amount: number;
  createdAt?: string | null;
  ctaLabel: string;
  ctaTo: string;
  sortScore: number;
  splitId?: string;
  expenseEventId?: string;
};

type ViewMode = 'all' | 'critical' | 'mine' | 'today_breaches';

const ageHours = (createdAt?: string | null): number => {
  if (!createdAt) return 0;
  const ts = new Date(createdAt).getTime();
  if (Number.isNaN(ts)) return 0;
  return Math.max(0, Math.floor((Date.now() - ts) / (1000 * 60 * 60)));
};

const isToday = (value?: string | null): boolean => {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear()
    && date.getMonth() === now.getMonth()
    && date.getDate() === now.getDate()
  );
};

const queueTypeMeta = (type: QueueItem['type']): { label: string; tone: 'neutral' | 'warning' | 'danger' } => {
  if (type === 'APPROVAL_PENDING') return { label: 'Pending', tone: 'neutral' };
  if (type === 'SLA_BREACH') return { label: 'SLA breach', tone: 'danger' };
  if (type === 'POLICY_VIOLATION') return { label: 'Policy violation', tone: 'warning' };
  return { label: 'Recon gap', tone: 'warning' };
};

const AdminControlTowerPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('all');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isRunningBulk, setIsRunningBulk] = useState(false);
  const [isEscalating, setIsEscalating] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [confirmApproveSplitId, setConfirmApproveSplitId] = useState<string | null>(null);
  const [streamStatus, setStreamStatus] = useState<'connecting' | 'live' | 'reconnecting'>('connecting');
  const [kpis, setKpis] = useState({
    pendingApprovals: 0,
    slaBreaches: 0,
    policyViolations: 0,
    reconciliationGaps: 0,
  });
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);

  const loadData = useCallback(async (options?: { silent?: boolean }) => {
    try {
      if (options?.silent) setIsRefreshing(true);
      else setIsLoading(true);
      setErrorMessage(null);
      setInfoMessage(null);

      const [events, breaches, reconciliationGaps, employees] = await Promise.all([
        getExpenseEvents({ perPage: 800 }),
        getAdminApprovalSlaBreaches({ perPage: 500 }),
        getReconciliationExceptionsReport({ perPage: 500 }),
        getCompanyEmployees(),
      ]);

      const employeeById = new Map(employees.map((row) => [row.id, row]));
      const pendingApprovals = events.flatMap((event) =>
        (event.splits || [])
          .filter((split) => normalizeWorkflowStatus(split.status) === 'pending')
          .map((split) => ({
            id: split.id,
            eventId: event.id,
            tripId: event.trip_id,
            amount: Number(split.split_amount || 0),
            createdAt: split.created_at || event.created_at,
            employeeName: employeeById.get(split.beneficiary_employee_id)?.name || employeeById.get(split.beneficiary_employee_id)?.email || 'Employee',
            managerName: employeeById.get(split.manager_id || '')?.name || employeeById.get(split.manager_id || '')?.email || 'Manager',
            category: event.category || 'Uncategorized',
          })),
      );

      const policyViolations = events
        .filter((event) => event.trust_signals?.flagged)
        .map((event) => ({
          id: event.id,
          tripId: event.trip_id,
          amount: Number(event.base_amount || event.total_amount || 0),
          createdAt: event.created_at,
          riskScore: Number(event.trust_signals?.risk_score || 0),
          reason: (event.trust_signals?.signals || []).map((item) => item.title).slice(0, 2).join(' | '),
        }));

      const unified: QueueItem[] = [
        ...breaches.map((row) => ({
          id: `sla-${row.split_id}`,
          type: 'SLA_BREACH' as const,
          severity: 'danger' as const,
          title: `SLA breach: split ${row.split_id.slice(0, 8)}`,
          detail: `${row.employee_name || 'Employee'} • ${row.department || 'No department'} • overdue ${Math.round(Number(row.overdue_hours || 0))}h`,
          owner: row.manager_name || 'Manager',
          amount: Number(row.split_amount || 0),
          createdAt: row.sla_due_at,
          ctaLabel: 'Open Team Approvals',
          ctaTo: '/admin/team-approvals',
          sortScore: 100000 + Number(row.overdue_hours || 0),
          splitId: row.split_id,
          expenseEventId: row.expense_event_id || undefined,
        })),
        ...pendingApprovals.map((row) => {
          const age = ageHours(row.createdAt);
          return {
            id: `pending-${row.id}`,
            type: 'APPROVAL_PENDING' as const,
            severity: age > 48 ? 'danger' as const : 'warning' as const,
            title: `Pending approval: split ${row.id.slice(0, 8)}`,
            detail: `${row.employeeName} • ${row.category} • age ${age}h`,
            owner: row.managerName,
            amount: row.amount,
            createdAt: row.createdAt,
            ctaLabel: 'Review Queue',
            ctaTo: '/admin/team-approvals',
            sortScore: 70000 + age,
            splitId: row.id,
            expenseEventId: row.eventId,
          };
        }),
        ...policyViolations.map((row) => ({
          id: `policy-${row.id}`,
          type: 'POLICY_VIOLATION' as const,
          severity: row.riskScore >= 70 ? 'danger' as const : 'warning' as const,
          title: `Policy/trust violation: event ${row.id.slice(0, 8)}`,
          detail: `${row.reason || 'Flagged by trust engine'} • risk score ${row.riskScore}`,
          owner: 'Finance Ops',
          amount: row.amount,
          createdAt: row.createdAt,
          ctaLabel: 'Open Event',
          ctaTo: `/admin/expense-events/${row.id}`,
          sortScore: 50000 + row.riskScore,
          expenseEventId: row.id,
        })),
        ...reconciliationGaps.map((row) => ({
          id: `recon-${row.expense_event_id}`,
          type: 'RECON_GAP' as const,
          severity: Math.abs(Number(row.delta || 0)) >= 1000 ? 'danger' as const : 'warning' as const,
          title: `Reconciliation gap: event ${row.expense_event_id.slice(0, 8)}`,
          detail: `${row.category || 'Uncategorized'} • delta ${formatCurrencyINRFull(Math.abs(Number(row.delta || 0)))}`,
          owner: 'Finance Controls',
          amount: Math.abs(Number(row.delta || 0)),
          createdAt: row.created_at,
          ctaLabel: 'Open Reconciliation',
          ctaTo: '/admin/settings',
          sortScore: 30000 + Math.abs(Number(row.delta || 0)),
          expenseEventId: row.expense_event_id,
        })),
      ]
        .sort((a, b) => b.sortScore - a.sortScore)
        .slice(0, 120);

      setKpis({
        pendingApprovals: pendingApprovals.length,
        slaBreaches: breaches.length,
        policyViolations: policyViolations.length,
        reconciliationGaps: reconciliationGaps.length,
      });
      setQueueItems(unified);
      setSelectedIds((prev) => prev.filter((id) => unified.some((item) => item.id === id)));
      setLastRefreshedAt(new Date());
    } catch (error) {
      if (isExpenseApiError(error)) setErrorMessage(error.message);
      else if (isApiError(error)) setErrorMessage(error.message);
      else if (error instanceof Error) setErrorMessage(error.message);
      else setErrorMessage('Failed to load finance command center.');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
    let refreshTimer: number | null = null;
    const queueSilentRefresh = () => {
      if (refreshTimer) return;
      refreshTimer = window.setTimeout(() => {
        refreshTimer = null;
        void loadData({ silent: true });
      }, 350);
    };
    const unsubscribe = subscribeControlTowerStream({
      onConnected: () => setStreamStatus('live'),
      onHeartbeat: () => setStreamStatus('live'),
      onEvent: (_event: ControlTowerStreamEvent) => {
        setStreamStatus('live');
        queueSilentRefresh();
      },
      onError: () => setStreamStatus('reconnecting'),
    });
    const safetySyncInterval = window.setInterval(() => {
      void loadData({ silent: true });
    }, 180000);
    return () => {
      if (refreshTimer) window.clearTimeout(refreshTimer);
      unsubscribe();
      window.clearInterval(safetySyncInterval);
    };
  }, [loadData]);

  const filteredQueue = useMemo(() => {
    if (viewMode === 'critical') return queueItems.filter((item) => item.severity === 'danger');
    if (viewMode === 'mine') return queueItems.filter((item) => (item.owner || '').toLowerCase().includes('finance'));
    if (viewMode === 'today_breaches') return queueItems.filter((item) => item.type === 'SLA_BREACH' && isToday(item.createdAt));
    return queueItems;
  }, [queueItems, viewMode]);

  const kpiTiles = useMemo(() => ([
    { key: 'pendingApprovals', label: 'Approvals pending', value: kpis.pendingApprovals },
    { key: 'slaBreaches', label: 'SLA breaches', value: kpis.slaBreaches },
    { key: 'policyViolations', label: 'Policy violations', value: kpis.policyViolations },
    { key: 'reconciliationGaps', label: 'Reconciliation gaps', value: kpis.reconciliationGaps },
  ]), [kpis]);

  const actionableSplitIds = useMemo(
    () => filteredQueue
      .filter((item) => selectedIds.includes(item.id))
      .filter((item) => item.type === 'SLA_BREACH' || item.type === 'APPROVAL_PENDING')
      .map((item) => item.splitId)
      .filter((id): id is string => Boolean(id)),
    [filteredQueue, selectedIds],
  );

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    const currentIds = filteredQueue.map((item) => item.id);
    const allSelected = currentIds.length > 0 && currentIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !currentIds.includes(id)));
      return;
    }
    setSelectedIds((prev) => Array.from(new Set([...prev, ...currentIds])));
  };

  const runBulkAction = async (action: 'APPROVED' | 'REJECTED' | 'REMIND') => {
    if (actionableSplitIds.length === 0) return;
    try {
      setErrorMessage(null);
      setInfoMessage(null);
      setIsRunningBulk(true);
      if (action === 'REMIND') {
        const result = await remindManagersForSplits({ split_ids: actionableSplitIds });
        setInfoMessage(`Reminders sent for ${result.sent} approval line(s).`);
      } else {
        const result = await bulkDecisionExpenseSplits({ split_ids: actionableSplitIds, decision: action });
        setInfoMessage(`${result.updated} updated, ${result.skipped} skipped.`);
      }
      await loadData();
    } catch (error) {
      if (isExpenseApiError(error)) setErrorMessage(error.message);
      else if (error instanceof Error) setErrorMessage(error.message);
      else setErrorMessage('Bulk action failed.');
    } finally {
      setIsRunningBulk(false);
    }
  };

  const runSingleAction = async (splitId: string | undefined, action: 'APPROVED' | 'REMIND') => {
    if (!splitId) return;
    try {
      setErrorMessage(null);
      setInfoMessage(null);
      setIsRunningBulk(true);
      if (action === 'REMIND') {
        const result = await remindManagersForSplits({ split_ids: [splitId] });
        setInfoMessage(`Reminder sent for ${result.sent} approval line.`);
      } else {
        const result = await bulkDecisionExpenseSplits({ split_ids: [splitId], decision: 'APPROVED' });
        setInfoMessage(`${result.updated} updated, ${result.skipped} skipped.`);
        setConfirmApproveSplitId(null);
      }
      await loadData({ silent: true });
    } catch (error) {
      if (isExpenseApiError(error)) setErrorMessage(error.message);
      else if (error instanceof Error) setErrorMessage(error.message);
      else setErrorMessage('Action failed.');
    } finally {
      setIsRunningBulk(false);
    }
  };

  const runEscalateNow = async () => {
    try {
      setErrorMessage(null);
      setInfoMessage(null);
      setIsEscalating(true);
      const result = await runEscalationScan();
      setInfoMessage(`Escalation scan complete. Overdue: ${result.overdue_count}. Escalated: ${result.escalated_count ?? 0}.`);
      await loadData({ silent: true });
    } catch (error) {
      if (isExpenseApiError(error)) setErrorMessage(error.message);
      else if (error instanceof Error) setErrorMessage(error.message);
      else setErrorMessage('Escalation scan failed.');
    } finally {
      setIsEscalating(false);
    }
  };

  const exportQueue = () => {
    const rows = filteredQueue.filter((item) => selectedIds.length === 0 || selectedIds.includes(item.id));
    const header = ['type', 'severity', 'title', 'detail', 'owner', 'amount', 'created_at', 'cta'];
    const escapeCell = (value: string | number | null | undefined): string => {
      const text = String(value ?? '');
      if (text.includes(',') || text.includes('"') || text.includes('\n')) return `"${text.replace(/"/g, '""')}"`;
      return text;
    };
    const csvRows = rows.map((item) => [
      item.type,
      item.severity,
      item.title,
      item.detail,
      item.owner,
      Number(item.amount || 0),
      item.createdAt || '',
      item.ctaTo,
    ]);
    const csv = [header.join(','), ...csvRows.map((row) => row.map((cell) => escapeCell(cell as string | number)).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.setAttribute('download', `finance-command-center-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  };

  const liveModeLabel = streamStatus === 'live'
    ? 'Live mode: real-time push active'
    : streamStatus === 'reconnecting'
      ? 'Live mode: reconnecting stream'
      : 'Live mode: connecting';

  return (
    <AdminShell
      pageTitle="Finance Command Center"
      subtitle="Action-first operations surface for approvals, SLA risk, policy violations, and reconciliation gaps."
    >
      {isLoading ? <LoadingDNA /> : null}
      {errorMessage ? <p className="rounded-lg bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{errorMessage}</p> : null}
      {infoMessage ? <p className="rounded-lg bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">{infoMessage}</p> : null}

      {!isLoading ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-600">
            <span className={`inline-block h-2.5 w-2.5 rounded-full ${streamStatus === 'live' ? 'bg-emerald-500' : streamStatus === 'reconnecting' ? 'bg-amber-500' : 'bg-slate-400'}`} />
            <span>{isRefreshing ? `${liveModeLabel} • syncing` : liveModeLabel}</span>
            <span className="text-slate-400">·</span>
            <span>Last refreshed: {lastRefreshedAt ? lastRefreshedAt.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}</span>
          </div>

          <div className="grid gap-3 md:grid-cols-4">
            {kpiTiles.map((tile) => {
              const isUrgent = tile.key === 'pendingApprovals' && tile.value > 0;
              const isMuted = tile.value === 0;
              const className = isUrgent
                ? 'border-amber-500 bg-amber-50 shadow-sm ring-1 ring-amber-200'
                : isMuted
                  ? 'border-slate-200/70 bg-slate-50/70'
                  : 'border-slate-200 bg-white';
              return (
                <article key={tile.key} className={`rounded-xl border px-4 py-3 ${className}`}>
                  <p className={`text-xs font-semibold ${isMuted ? 'text-slate-400' : 'text-slate-600'}`}>{tile.label}</p>
                  <p className={`mt-1 font-extrabold tracking-tight ${isUrgent ? 'text-4xl text-amber-900' : isMuted ? 'text-3xl text-slate-300' : 'text-3xl text-slate-900'}`}>{tile.value}</p>
                </article>
              );
            })}
          </div>

          <SectionCard
            title="Unified Priority Queue"
            subtitle="Ranked by severity and risk score so finance can execute from one screen."
            actions={
              <div className="flex flex-wrap items-center justify-end gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-slate-500">Filter</span>
                  <button
                    type="button"
                    onClick={() => setViewMode('all')}
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${viewMode === 'all' ? 'bg-slate-900 text-white' : 'border border-slate-300 text-slate-700 hover:bg-slate-50'}`}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('critical')}
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${viewMode === 'critical' ? 'bg-slate-900 text-white' : 'border border-slate-300 text-slate-700 hover:bg-slate-50'}`}
                  >
                    Critical
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('mine')}
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${viewMode === 'mine' ? 'bg-slate-900 text-white' : 'border border-slate-300 text-slate-700 hover:bg-slate-50'}`}
                  >
                    My items
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('today_breaches')}
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${viewMode === 'today_breaches' ? 'bg-slate-900 text-white' : 'border border-slate-300 text-slate-700 hover:bg-slate-50'}`}
                  >
                    Today breaches
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-slate-500">Actions</span>
                  <button
                    type="button"
                    onClick={() => loadData({ silent: true })}
                    className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Refresh
                  </button>
                  <button
                    type="button"
                    onClick={exportQueue}
                    className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Export
                  </button>
                  <button
                    type="button"
                    disabled={isEscalating}
                    onClick={runEscalateNow}
                    className="rounded-full border border-red-300 px-3 py-1 text-xs font-semibold text-red-700 disabled:opacity-60"
                  >
                    {isEscalating ? 'Escalating...' : 'Escalate'}
                  </button>
                  {selectedIds.length > 0 ? <span className="text-xs font-semibold text-slate-600">{selectedIds.length} selected</span> : null}
                  {actionableSplitIds.length > 0 ? (
                    <button
                      type="button"
                      disabled={isRunningBulk}
                      onClick={() => runBulkAction('APPROVED')}
                      className="rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-60"
                    >
                      Bulk approve
                    </button>
                  ) : null}
                  {actionableSplitIds.length > 0 ? (
                    <button
                      type="button"
                      disabled={isRunningBulk}
                      onClick={() => runBulkAction('REJECTED')}
                      className="rounded-full bg-red-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-60"
                    >
                      Bulk reject
                    </button>
                  ) : null}
                  {actionableSplitIds.length > 0 ? (
                    <button
                      type="button"
                      disabled={isRunningBulk}
                      onClick={() => runBulkAction('REMIND')}
                      className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800 disabled:opacity-60"
                    >
                      Bulk remind
                    </button>
                  ) : null}
                </div>
              </div>
            }
          >
            <div className="mb-3 flex items-center justify-end">
              <span className="text-xs font-semibold text-slate-500">Showing {filteredQueue.length} items</span>
            </div>

            {filteredQueue.length === 0 ? (
              <p className="text-sm font-semibold text-slate-600">No command-center exceptions. All queues are clear.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1080px]">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs font-semibold text-slate-600">
                      <th className="pb-2">
                        <input
                          type="checkbox"
                          checked={filteredQueue.length > 0 && filteredQueue.every((row) => selectedIds.includes(row.id))}
                          onChange={toggleSelectAll}
                        />
                      </th>
                      <th className="pb-2">Type</th>
                      <th className="pb-2">Issue</th>
                      <th className="pb-2">Action</th>
                      <th className="pb-2 text-right">Amount</th>
                      <th className="pb-2">Date</th>
                      <th className="pb-2">Owner</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredQueue.map((item) => (
                      <tr key={item.id} className="border-b border-slate-100 text-sm text-slate-700">
                        <td className="py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(item.id)}
                            onChange={() => toggleSelect(item.id)}
                          />
                        </td>
                        <td className="py-3">
                          <StatusBadge label={queueTypeMeta(item.type).label} tone={queueTypeMeta(item.type).tone} />
                        </td>
                        <td className="py-3">
                          <p className="font-semibold text-slate-900">{item.title}</p>
                          <p className="text-xs text-slate-600">{item.detail}</p>
                          <Link
                            to={item.ctaTo}
                            className="mt-1 inline-flex items-center text-xs font-semibold text-slate-500 hover:text-slate-700"
                          >
                            {item.ctaLabel}
                          </Link>
                        </td>
                        <td className="py-3">
                          <div className="flex flex-wrap items-center gap-2">
                            {(item.type === 'SLA_BREACH' || item.type === 'APPROVAL_PENDING') && item.splitId ? (
                              confirmApproveSplitId === item.splitId ? (
                                <div className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1">
                                  <span className="text-xs font-semibold text-emerald-800">Confirm approve?</span>
                                  <button
                                    type="button"
                                    disabled={isRunningBulk}
                                    onClick={() => {
                                      void runSingleAction(item.splitId, 'APPROVED');
                                    }}
                                    className="rounded-md bg-emerald-600 px-2 py-1 text-xs font-semibold text-white disabled:opacity-60"
                                  >
                                    Yes
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setConfirmApproveSplitId(null)}
                                    className="rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700"
                                  >
                                    No
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  disabled={isRunningBulk}
                                  onClick={() => setConfirmApproveSplitId(item.splitId || null)}
                                  className="inline-flex items-center rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-60"
                                >
                                  Approve
                                </button>
                              )
                            ) : null}
                            {(item.type === 'SLA_BREACH' || item.type === 'APPROVAL_PENDING') && item.splitId ? (
                              <button
                                type="button"
                                disabled={isRunningBulk}
                                onClick={() => runSingleAction(item.splitId, 'REMIND')}
                                className="inline-flex items-center rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-600 disabled:opacity-60"
                              >
                                Remind
                              </button>
                            ) : null}
                          </div>
                        </td>
                        <td className="py-3 text-right font-semibold text-slate-900">{formatCurrencyINRFull(item.amount)}</td>
                        <td className="py-3">{formatDateIN(item.createdAt ? new Date(item.createdAt) : null)}</td>
                        <td className="py-3">{item.owner}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {filteredQueue.length === 1 ? (
              <div className="mt-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">
                No other items in queue.
              </div>
            ) : null}
          </SectionCard>
        </div>
      ) : null}
    </AdminShell>
  );
};

export default AdminControlTowerPage;
